import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { FILE_VIDEO_PATTERN } from '../../api/filePreview';
import { canPreviewMedia, FilePreview, type PreviewSource } from './FilePreview';
import './file-gallery.css';
import { FolderMobileVideo } from './FolderMobileVideo';
import './public-folder-video.css';
import './public-image-fullscreen.css';

export function FileGallery({ files, initialName, onClose, mobileTouch: initialMobileTouch }: { files: PreviewSource[]; initialName: string; onClose: () => void; mobileTouch?: boolean }) {
  // Rotation/fullscreen must not replace an active mobile stream with the desktop original.
  const [mobileTouch] = useState(Boolean(initialMobileTouch));
  // Preserve the folder's visible order throughout this viewing session.
  const [items] = useState(files);
  const [index, setIndex] = useState(() => Math.max(0, files.findIndex(file => file.name === initialName)));
  const stage = useRef<HTMLDivElement>(null);
  const [quiet, setQuiet] = useState(false);
  const file = items[index];
  const move = (direction: number) => {
    stage.current?.focus({ preventScroll: true });
    setIndex(current => Math.max(0, Math.min(items.length - 1, current + direction)));
  };
  const handleKey = useEffectEvent((event: KeyboardEvent) => {
    const dialog = stage.current?.closest('[role="dialog"]');
    if (!dialog || [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].at(-1) !== dialog) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || target?.isContentEditable || target?.closest('input,textarea,select,[data-video-control-popover]')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    move(event.key === 'ArrowLeft' ? -1 : 1);
  });
  useEffect(() => {
    const key = (event: KeyboardEvent) => handleKey(event);
    // Before the player's frame shortcuts, and also when focus was on a removed control.
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, []);
  if (!file) return null;
  if (mobileTouch && !canPreviewMedia(file.name)) return <FilePreview key={file.original} file={file} mobilePdf onClose={onClose} touchNavigation={move}/>;
  const video = FILE_VIDEO_PATTERN.test(file.name);
  return <Modal title={file.name} size="wide" className={`file-gallery ${video ? 'video-review review-redesign' : 'chat-media-viewer'} ${mobileTouch&&video ? `sx-folder-video-gallery ${quiet?'is-quiet':''}` : ''}`} onClose={onClose}
    immersiveFullscreen={mobileTouch && !video}
    headerToolbar={<><span className="file-gallery-count" aria-live="polite" aria-atomic="true">{index + 1} / {items.length}</span>{mobileTouch&&file.allowDownload!==false&&<a className="icon-button" href={file.download} download={file.name} aria-label="Скачать файл"><Download size={20}/></a>}</>}
    onKeyDown={event => event.stopPropagation()}>
    <div className="file-gallery-stage" ref={stage} tabIndex={-1} onPointerDown={event => event.stopPropagation()}>
      {mobileTouch&&video ? <FolderMobileVideo key={file.original} file={file} onNavigate={move} onQuiet={setQuiet}/> : <FilePreview key={file.original} file={file} embedded onClose={onClose} touchNavigation={mobileTouch&&!video?move:undefined}/>}
      {items.length > 1 && !mobileTouch && <>
        <button type="button" className="file-gallery-arrow file-gallery-arrow--previous" aria-label="Предыдущий файл" title="Предыдущий файл (←)" disabled={index === 0} onClick={() => move(-1)}><ChevronLeft size={32}/></button>
        <button type="button" className="file-gallery-arrow file-gallery-arrow--next" aria-label="Следующий файл" title="Следующий файл (→)" disabled={index === items.length - 1} onClick={() => move(1)}><ChevronRight size={32}/></button>
      </>}
    </div>
  </Modal>;
}

